import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import AnimatedSpotInnovation from '@utilitywarehouse/hearth-json-assets/lib/animated-spot-innovation-light.json';
import { Box, Flex, Heading } from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import SliderImport from 'react-slick';

import {
  DialogProps as BaseDialogProps,
  DialogID,
  DialogUnwrapped,
  useDialog,
} from '@components/Dialog';
import { EnergyBreakdown } from '@components/EnergyBreakdown';
import { getEnergyTariffByType } from '@features/Energy/utils';
import {
  ChargeAmount,
  EnergyFuelType,
  EnergyQuote,
  EnergyQuoteType,
} from '@generated/gql/graphql';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { UpdatedTariffModalState } from '@providers/basket/types';
import { UpdatedTariffModalVariant } from '@src/types/bundle';
import { useBreakpoints } from '@utils/useBreakpoints';

import { TariffForm } from './TariffForm';
import './styles.scss';
import { useBreakdownSlider } from './useBreakdownSlider';

type DialogProps = Omit<BaseDialogProps, 'loading' | 'loadingText'> & {
  syncedLoading: boolean;
  heading: string;
};

interface UpdatedEnergyModalProps {
  fuelType: EnergyFuelType;
  tariffs: EnergyQuote[];
  isLoading: boolean;
  isOpen: boolean;
  modalState: UpdatedTariffModalState;
  selectedPrice: Nullable<ChargeAmount>;
  initialQuoteType: EnergyQuoteType;
  onSubmit: (quoteType: EnergyQuoteType) => Promise<void>;
}

// @ts-expect-error https://github.com/akiran/react-slick/issues/2444
const Slider = SliderImport.default ?? SliderImport;

const getHasConfetti = (variant: UpdatedTariffModalVariant) =>
  [
    UpdatedTariffModalVariant.priceDecreased,
    UpdatedTariffModalVariant.productAdded,
  ].includes(variant);

const getHeadingText = (
  variant: UpdatedTariffModalVariant,
  hasChoice: boolean
) => {
  switch (variant) {
    case UpdatedTariffModalVariant.productRemoved:
      return hasChoice
        ? 'Choose your new energy tariff'
        : 'Your new energy tariff will be:';
    case UpdatedTariffModalVariant.productAdded:
      return 'You’ve unlocked cheaper energy!';
    case UpdatedTariffModalVariant.priceDecreased:
      return 'Our energy prices have gone down!';
    case UpdatedTariffModalVariant.priceIncreased:
      return 'Our energy tariffs have changed';
    case UpdatedTariffModalVariant.bundleExpired:
      return 'Your energy options have changed';
    case UpdatedTariffModalVariant.initial:
    default:
      return 'Your energy options have changed';
  }
};

const Dialog = ({ id, children, syncedLoading, ...props }: DialogProps) => {
  const { open, setOpen } = useDialog(id);
  useEffect(() => () => setOpen(false), [setOpen]);
  return (
    // syncedLoading keeps dialog open for passive client
    // while refetching the basket after active client's update tariff mutation completes
    <DialogUnwrapped {...props} open={syncedLoading || open}>
      {children}
    </DialogUnwrapped>
  );
};

const getTestId = (variant: UpdatedTariffModalVariant) => {
  switch (variant) {
    case UpdatedTariffModalVariant.priceIncreased:
      return 'price-increased-dialog';

    case UpdatedTariffModalVariant.priceDecreased:
      return 'price-decreased-dialog';

    case UpdatedTariffModalVariant.productAdded:
      return 'cheaper-tariff-change-dialog';

    case UpdatedTariffModalVariant.productRemoved:
      return 'removal-tariff-change-dialog';

    case UpdatedTariffModalVariant.initial:
    case UpdatedTariffModalVariant.bundleExpired:
    default:
      return 'bundle-changed-dialog';
  }
};

const UpdatedEnergyModal = ({
  modalState,
  tariffs,
  fuelType,
  isOpen,
  isLoading,
  onSubmit,
  ...props
}: UpdatedEnergyModalProps) => {
  const { hasControl, isSyncedAppointment } = useAppointmentContext();
  const { breakpointsDown } = useBreakpoints();
  const {
    breakdownVariant,
    sliderRef,
    sliderSettings,
    handleSliderChange,
    handleBreakdownBack,
    handleBreakdownClick,
  } = useBreakdownSlider();
  const fullScreen = breakpointsDown.sm;
  const { variant } = modalState;
  const dialog = useDialog(DialogID.UPDATED_ENERGY_TARIFF);
  const [error, setError] = useState<Nullable<Error>>(null);

  const fixedTariff = getEnergyTariffByType(tariffs, EnergyQuoteType.Fixed);
  const trackerTariff = getEnergyTariffByType(tariffs, EnergyQuoteType.Tracker);
  const variableTariff = getEnergyTariffByType(
    tariffs,
    EnergyQuoteType.Variable
  );

  // Re-throw Formik error during render to bubble up to ErrorBoundary
  if (error) {
    throw error;
  }

  const effectiveVariant = hasControl
    ? variant
    : (dialog.variant as UpdatedTariffModalVariant) || variant;
  const testId = getTestId(effectiveVariant);

  const isPassiveClient = !hasControl && isSyncedAppointment;
  const hasConfetti = getHasConfetti(effectiveVariant);

  useEffect(() => {
    dialog.setOpen(isOpen);
  }, [dialog, isOpen]);

  useEffect(() => {
    dialog.setVariant(variant);
  }, [dialog, variant]);

  useEffect(() => {
    if (isPassiveClient) return;
    dialog.setIsLoading(isLoading);
  }, [dialog, isLoading, isPassiveClient]);

  const handleSubmit = async (quoteType: EnergyQuoteType) => {
    try {
      await onSubmit(quoteType);
    } catch (error) {
      setError(error as Error);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex
          flexGrow="1"
          alignSelf="stretch"
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap="300"
          padding="400"
        >
          <Box width="120px" height="135px">
            <DotLottieReact data={AnimatedSpotInnovation} loop autoplay />
          </Box>
          <Heading as="h2" size="lg" textAlign="center">
            Updating your tariff...
          </Heading>
        </Flex>
      );
    }

    if (!variableTariff) {
      return null;
    }

    return (
      <Slider
        {...sliderSettings}
        className="energy-tariff-modal__breakdown-slider"
        ref={sliderRef}
        beforeChange={handleSliderChange}
      >
        <Box
          className={clsx('energy-tariff-modal__breakdown-slider__item', {
            'energy-tariff-modal__breakdown-slider__item--confetti':
              hasConfetti,
          })}
        >
          <TariffForm
            {...props}
            hasConfetti={hasConfetti}
            onSubmit={handleSubmit}
            modalState={{ ...modalState, variant: effectiveVariant }}
            tariffs={tariffs}
            onBreakdownClick={handleBreakdownClick}
            isPassiveClient={isPassiveClient}
            heading={getHeadingText(
              effectiveVariant,
              !!(trackerTariff || fixedTariff)
            )}
          />
        </Box>
        <Box
          className={clsx(
            'energy-tariff-modal__breakdown-slider__item',
            'energy-tariff-modal__breakdown-slider__item--breakdown'
          )}
        >
          <EnergyBreakdown
            quote={
              breakdownVariant === EnergyQuoteType.Variable
                ? variableTariff
                : breakdownVariant === EnergyQuoteType.Tracker
                  ? (trackerTariff as EnergyQuote)
                  : (fixedTariff as EnergyQuote)
            }
            fuelType={fuelType}
            onBack={handleBreakdownBack}
          />
        </Box>
      </Slider>
    );
  };

  return (
    <Dialog
      className="energy-tariff-modal__dialog"
      id={dialog.id}
      fullScreen={fullScreen}
      syncedLoading={isPassiveClient && isLoading}
      data-testid={testId}
      heading=""
      preventDismiss
      hideCloseButton
    >
      {renderContent()}
    </Dialog>
  );
};

export { UpdatedEnergyModal };
